'use client';
import { alpha, Box, Button, Theme, Typography } from '@mui/material';

import type { EventOutcome } from '@/types';
import type { CardVariantProps } from './types';

interface CustomOutcomeRowProps {
  outcomeName: string;
  onTrade: () => void;
  percentage: number;
  idx: number;
}

const getButtonStyles = (theme: Theme, idx: number) => {
  const variant = idx === 0 ? theme.palette.success : theme.palette.error;
  return {
    minWidth: 54,
    height: 32,
    borderRadius: '8px',
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '0.8rem',
    boxShadow: 'none',
    bgcolor: variant.bg,
    color: theme.palette.mode === 'dark' ? variant.light : variant.dark,
    border: '1px solid',
    borderColor: variant.border,
    '&:hover': {
      bgcolor: alpha(variant.main, 0.25),
      boxShadow: 'none',
    },
  };
};

export function CustomOutcomeRow({ outcomeName, onTrade, percentage, idx }: CustomOutcomeRowProps) {
  const handleTrade = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onTrade();
  };

  const sxStyles = (theme: Theme) => getButtonStyles(theme, idx);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        py: 0.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}
      >
        {outcomeName}
      </Typography>
      <Button variant="contained" size="small" onClick={handleTrade} sx={sxStyles}>
        {percentage}%
      </Button>
    </Box>
  );
}

interface CustomOutcomeButtonProps {
  outcome: EventOutcome;
  index: number;
  marketId: string | undefined;
  onTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
}

function CustomOutcomeButton({ outcome, index, marketId, onTradeClick }: CustomOutcomeButtonProps) {
  const handleTrade = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (marketId && outcome.id) {
      onTradeClick?.(marketId, outcome.id, true);
    }
  };

  const isFirst = index === 0;

  return (
    <Button
      onClick={handleTrade}
      sx={{
        flex: 1,
        minWidth: 0, // Critical for text truncation in flex items
        height: 44,
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.95rem',
        boxShadow: 'none',
        bgcolor: (theme) => {
          const variant = isFirst ? theme.palette.success : theme.palette.error;
          return alpha(variant.main, 0.1);
        },
        color: (theme) => {
          const variant = isFirst ? theme.palette.success : theme.palette.error;
          return theme.palette.mode === 'dark' ? variant.light : variant.dark;
        },
        border: '1px solid',
        borderColor: (theme) => {
          const variant = isFirst ? theme.palette.success : theme.palette.error;
          return alpha(variant.main, 0.3);
        },
        '&:hover': {
          bgcolor: (theme) => {
            const variant = isFirst ? theme.palette.success : theme.palette.error;
            return alpha(variant.main, 0.2);
          },
          borderColor: (theme) => {
            const variant = isFirst ? theme.palette.success : theme.palette.error;
            return variant.light;
          },
          boxShadow: 'none',
        },
      }}
    >
      <Box
        component="span"
        sx={{
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          textAlign: 'center',
        }}
      >
        {outcome.name}
      </Box>
    </Button>
  );
}

export function CustomBinaryCard({ market, onTradeClick }: CardVariantProps) {
  const outcomes = Array.isArray(market.outcomes) ? (market.outcomes as EventOutcome[]) : [];
  if (outcomes.length === 0) return null;

  const visibleOutcomes = outcomes.slice(0, 2);

  const renderVisibleOutcome = (outcome, i) => (
    <CustomOutcomeButton
      key={outcome.id || i}
      outcome={outcome}
      index={i}
      marketId={market.id}
      onTradeClick={onTradeClick}
    />
  );

  const outcomesRows = visibleOutcomes.map(renderVisibleOutcome);

  return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1.5 }}>{outcomesRows}</Box>;
}
